import React, { memo, useCallback, useMemo } from "react";
import { Box } from "./Box";
import { IMessageUser } from "@microdoing/shared/services/message/types";
import FastImage from "react-native-fast-image";
import EmptyPicture from "./EmptyPicture";
import theme from "app/Theme/Theme";
import { StyleSheet } from "react-native";
import normalize from "app/utils/normalize";
import PictureName from "./PictureName";

export interface IMultipleAvatarSquareProps {
  users: IMessageUser[];
  size?: number;
  pictureSize?: number;
}

const MultipleAvatarSquare = ({
  users,
  size = 65,
}: IMultipleAvatarSquareProps) => {
  const pictureSize = useMemo(() => (users.length > 1 ? size * 0.6 : size), [
    users.length,
    size,
  ]);

  const numberOfPictures = useMemo(() => {
    return users.length > 4 ? 4 : users.length;
  }, [users.length]);

  const pictureStyles = useMemo(() => {
    return [
      [{}],
      [styles.smallPictureTopLeft, styles.smallPictureBottomRight],
      [
        styles.smallPictureBottomLeft,
        styles.smallPictureTopRight,
        styles.smallPictureBottom,
      ],
      [
        styles.smallPictureTopLeft,
        styles.smallPictureTopRight,
        styles.smallPictureBottomLeft,
        styles.smallPictureBottomRight,
      ],
    ];
  }, [users.length]);

  const usersToDisplay = useMemo(() => {
    return users.slice(0, 4);
  }, [users]);

  return (
    <Box alignItems="center" justifyContent="center" width={size} height={size}>
      {usersToDisplay.map((user, index) => {
        return (
          <PictureName
            key={user._id}
            pictureSize={pictureSize}
            imageUrl={user?.picture}
            name={user?.name}
            style={pictureStyles[numberOfPictures - 1][index]}
          />
        );
      })}
    </Box>
  );
};

const styles = StyleSheet.create({
  smallPictureTopLeft: {
    zIndex: 1,
    borderColor: theme.colors["neutral.white"],
    borderWidth: 1,
    borderStyle: "solid",
    position: "absolute",
    left: 0,
    top: 0,
  },
  smallPictureTopRight: {
    zIndex: 2,
    borderColor: theme.colors["neutral.white"],
    borderWidth: 1,
    borderStyle: "solid",
    position: "absolute",
    right: 0,
    top: 0,
  },
  smallPictureBottomLeft: {
    zIndex: 3,
    borderWidth: 1,
    borderColor: theme.colors["neutral.white"],
    borderStyle: "solid",
    position: "absolute",
    bottom: 0,
    left: 0,
  },
  smallPictureBottomRight: {
    borderWidth: 1,
    borderColor: theme.colors["neutral.white"],
    borderStyle: "solid",
    zIndex: 4,
    position: "absolute",
    bottom: 0,
    right: 0,
  },
  smallPictureBottom: {
    borderWidth: 1,
    borderColor: theme.colors["neutral.white"],
    borderStyle: "solid",
    zIndex: 4,
    position: "absolute",
    alignSelf: "center",
    bottom: 0,
  },
});

export default memo(MultipleAvatarSquare);
